/**
 * @module pages/Order/PackageStatusPoller
 * @description Headless React component for polling Aspera package transfer status.
 * Automatically polls transfer status for running packages and updates order state when complete.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLambdaPoll } from 'src/modules/useLambda';
import { Assets } from 'src/types/assets/types';
import { useOrders } from 'src/providers/OrdersProvider';
import { identityHeadersPromise } from 'src/providers/IdentityProvider';
import { PackageStatusPollerProps, StatusRequest, StatusResponse } from './types';

/**
 * @const TERMINAL_STATUSES
 * @description Set of transfer statuses that indicate polling should stop
 * @type {ReadonlySet<Assets.Types.TransferStatus>}
 */
const TERMINAL_STATUSES: ReadonlySet<Assets.Types.TransferStatus> = new Set<Assets.Types.TransferStatus>([
  Assets.Types.TransferStatus.CANCELED,
  Assets.Types.TransferStatus.COMPLETED,
  Assets.Types.TransferStatus.FAILED,
]);

/**
 * @component PackageStatusPoller
 * @summary Headless component that polls Aspera package transfer status and updates parent order state.
 * Auto-starts polling when package status is RUNNING, auto-stops when terminal status reached.
 * @param {PackageStatusPollerProps} props component props
 * @returns {null} Renders nothing (headless component)
 */
export const PackageStatusPoller: React.FC<PackageStatusPollerProps> = ({ orderId, pkg, onReplaceOrder }) => {
  const { refreshOrder } = useOrders();
  const [headers, setHeaders] = useState<HeadersInit | null>(null);

  // Fetch identity headers once so lambda calls are authorized
  useEffect(() => {
    let mounted = true;
    identityHeadersPromise().then((h) => {
      if (mounted) setHeaders(h);
    });
    return () => {
      mounted = false;
    };
  }, []);

  const getPayload = useCallback<() => StatusRequest>(() => ({
    type: Assets.Types.Operation.STATUS,
    data: { orderId, packageId: pkg._key },
  }), [orderId, pkg._key]);

  const poll = useLambdaPoll<StatusRequest, StatusResponse>({
    functionName: 'assets',
    autoStart: false,
    getPayload,
    headers: headers || undefined,
  });

  const isAspera = pkg.assetProvider === Assets.Types.Providers.ASPERA;
  const isPkgRunning = useMemo(
    () => pkg.transferStatus === Assets.Types.TransferStatus.RUNNING,
    [pkg.transferStatus],
  );
  const { isRunning, start, stop, lastResult } = poll;

  // Auto-start polling when the package is running and we have headers
  useEffect(() => {
    if (!headers) return;
    if (isAspera && isPkgRunning && !isRunning) {
      start();
    }
  }, [headers, isAspera, isPkgRunning, isRunning, start]);

  // Stop polling if the incoming pkg status becomes terminal
  useEffect(() => {
    if (!isRunning) return;
    if (pkg.transferStatus && TERMINAL_STATUSES.has(pkg.transferStatus)) {
      stop();
    }
  }, [pkg.transferStatus, isRunning, stop]);

  // On poll result, refresh the order and stop if the package reached a terminal state
  useEffect(() => {
    if (!lastResult) return;
    (async () => {
      const fresh = await refreshOrder(orderId);
      if (fresh) {
        onReplaceOrder?.(fresh);
        const nextPkg = (fresh.packages || []).find((p) => p._key === pkg._key);
        const nextStatus = nextPkg?.transferStatus;
        if (nextStatus && TERMINAL_STATUSES.has(nextStatus)) {
          stop();
        }
      }
    })();
  }, [lastResult, refreshOrder, orderId, onReplaceOrder, pkg._key, stop]);

  return null;
};

export default PackageStatusPoller;
